import React, { useState } from 'react';
import { useFeathers } from '../../app/util';
import styled from 'styled-components';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import TranslateIcon from '@mui/icons-material/Translate';
import RefreshIcon from '@mui/icons-material/Refresh';
import { languageConfig } from '../../language.config';
import { IconButton } from '@mui/material';

export const RetranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .refresh-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

// Filter out English since we don't need to translate to English
const languages = languageConfig.filter(lang => lang.code !== 'en');

const RetranslateTrigger = ({ serviceName, objectId, style = {}, trigger }) => {
  const feathers = useFeathers();
  const [open, setOpen] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedLanguages({});
    setError(null);
  };

  const handleToggle = (languageCode) => {
    setSelectedLanguages(prev => ({
      ...prev,
      [languageCode]: !prev[languageCode]
    }));
  };

  const handleSubmit = async () => {
    const selectedCodes = Object.entries(selectedLanguages)
      .filter(([_, selected]) => selected)
      .map(([code]) => code);

    if (selectedCodes.length === 0) {
      setError('Please select at least one language');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await Promise.all(
        selectedCodes.map(language =>
          feathers.getService('translate').patch(null, {}, {
            query: {
              language,
              service: serviceName,
              _id: objectId
            }
          })
        )
      );
      handleClose();
    } catch (err) {
      console.error('Translation error:', err);
      setError('Failed to trigger translation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: handleClickOpen })
      ) : (
        <IconButton
          variant="text"
          onClick={handleClickOpen}
          style={style}
          color="primary"
        >
          <RetranslateIcon>
            <TranslateIcon fontSize="small" />
            <RefreshIcon className="refresh-icon" fontSize="small" />
          </RetranslateIcon>
        </IconButton>
      )}

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Select Languages to Retranslate</DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" style={{ marginBottom: '16px' }}>
              {error}
            </Typography>
          )}
          <List>
            {languages.map((language) => (
              <ListItem
                key={language.code}
                button
                onClick={() => handleToggle(language.code)}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={!!selectedLanguages[language.code]}
                    tabIndex={-1}
                    disableRipple
                  />
                </ListItemIcon>
                <ListItemText primary={language.name} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export { RetranslateTrigger }; 